import { type FC, useCallback } from "react";
import type { UUID } from "../../utils/uuid";
import { type AssetResult, useUpdateAssetCallback } from "./hooks/use-upsert-asset";
import type { Asset } from "./server/digital-asset-service";
import { UploadFileModal } from "./upload-file-modal";

export const ReplaceFileModal: FC<{
  open: boolean;
  assetToReplace: Asset;
  onOpen: () => void;
  onClose: () => void;
  onCancel: () => void;
  onChange: () => void;
  onViewFileClick: (asset: Asset) => void;
}> = ({ assetToReplace, open, ...props }) => {
  const updateAsset = useUpdateAssetCallback();

  const handleSave = useCallback(
    async (asset: {
      title: string;
      thumbnailUrl?: string | null;
      contentType: string;
      projectId?: string | null;
      pageId?: string | null;
      mimeType?: string | null;
      sourceDigitalAssetId?: UUID | null;
      publicUrl?: string | null;
      externalStorageId?: string | null;
    }): Promise<AssetResult> => {
      if (!asset.publicUrl || !assetToReplace.public_url)
        throw new Error("Asset must have a public url");

      return await updateAsset({
        asset: assetToReplace,
        title: asset.title,
        ...(asset.thumbnailUrl ? { thumbnailUrl: asset.thumbnailUrl } : {}),
        publicUrl: asset.publicUrl,
        externalStorageId: asset.externalStorageId,
      });
    },
    [assetToReplace, updateAsset],
  );

  const allowedFileTypes = (asset: Asset): ("image" | "video" | "pdf")[] => {
    if (!asset) return [];
    if (asset.mime_type?.startsWith("image/")) return ["image"];
    if (asset.mime_type?.startsWith("video/")) return ["video"];
    return ["pdf"];
  };

  return (
    open && (
      <UploadFileModal
        open
        heading={`Replace ${assetToReplace.title}`}
        allowedFileTypes={allowedFileTypes(assetToReplace)}
        onSave={handleSave}
        assetToReplaceId={assetToReplace.id}
        {...props}
      />
    )
  );
};
